import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  LayoutContextProvider,
  LiveKitRoom,
  RoomAudioRenderer,
  useCreateLayoutContext,
  useRoomContext,
} from '@livekit/components-react'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { useMutation } from '@tanstack/react-query'
import type { MediaDeviceFailure } from 'livekit-client'
import { ShadcnScope } from '@/custolink-newdesign/components/ShadcnScope'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/custolink-newdesign/ui/sheet'
import {
  AutoEnableTracks,
  mediaFailureMessage,
  useVideoRoomConnection,
} from '@/custolink-views/shared/useVideoRoomConnection'
import type { VideoRoomEndedInfo } from '@/custolink-views/shared/useVideoRoomConnection'
import { tokenCanPublish } from '@/custolink-views/shared/recording_consent/recordingStatus'
import {
  workerRecordingConsentApi,
  type RecordingConsentApi,
} from '@/custolink-views/shared/recording_consent/recordingConsentApi'
import { useRecordingConsent } from '@/custolink-views/shared/recording_consent/useRecordingConsent'
import { RoomSyncProvider, useRoomSyncOptional } from '@/custolink-newdesign/remote-support-sync/RoomSyncProvider'
import { StageSyncLayer } from '@/custolink-newdesign/remote-support-sync/StageSyncLayer'
import { workerSyncApi, type SyncActiveSourceInput } from '@/custolink-newdesign/remote-support-sync/syncApi'
import { RoomHeader } from './RoomHeader'
import { RoomBlockingCard } from './RoomBlockingCard'
import { RecordingConsentCard } from './RecordingConsentCard'
import { EquipmentStage } from './EquipmentStage'
import { CallBar } from './CallBar'

const WIDE_QUERY = '(min-width: 1024px)'

/** Same matchMedia pattern as AppShell's useIsMobile, mirrored at the opposite breakpoint. */
function useIsWide(): boolean {
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(WIDE_QUERY).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY)
    const on = () => setIsWide(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return isWide
}

type RoomIdentity = {
  title: string
  subtitle?: string
  details?: string[]
  elapsedAnchorMs: number | null
}

/**
 * Must be mounted inside LiveKitRoom and LayoutContextProvider - it reads the room via
 * useRoomContext() for the Leave control (a plain disconnect, never an API call, D-01).
 */
function WorkerRoomBody(p: {
  identity: RoomIdentity
  recording: boolean
  consentApi: RecordingConsentApi | null
  token: string
  inviteLabel?: string
  sidePanel?: ReactNode
  sidePanelLabel?: string
  sidePanelOpen: boolean
  onSidePanelOpenChange: (open: boolean) => void
  isWide: boolean
  thumbnailsCollapsed: boolean
  onToggleThumbnails: () => void
  canEndSession: boolean
  onEndSession: () => void
  endSessionPending: boolean
  onMediaFailure: (failure: MediaDeviceFailure) => void
}) {
  const room = useRoomContext()
  const sync = useRoomSyncOptional()

  // D-05/D-09/D-06 for the worker: the same hook the default room mounts, over the org-scoped
  // admin consent API. Null (no orgId/callId) means no consent surface at all.
  const consent = useRecordingConsent({
    api: p.consentApi,
    token: p.token,
    role: 'worker',
    onMediaFailure: p.onMediaFailure,
  })

  // Publishes this expert's explicit stage pick as the session's shared active source (D-03):
  // the technician's stage then follows it via EquipmentStage's followTrackSid. Nothing here ever
  // reacts to who is speaking.
  const setActiveSourceMutation = useMutation({
    mutationFn: (src: SyncActiveSourceInput) => {
      if (sync?.api.worker == null) {
        throw new Error('setActiveSource is worker-only')
      }
      return sync.api.worker.setActiveSource(src)
    },
    onSuccess: (view) => sync?.applyServerState(view),
  })

  const handleUserSelectedStage = (t: TrackReferenceOrPlaceholder) => {
    if (t.publication?.trackSid == null) {
      return
    }
    setActiveSourceMutation.mutate({ trackSid: t.publication.trackSid, identity: t.participant.identity })
  }

  return (
    <>
      <RoomHeader
        title={p.identity.title}
        subtitle={p.identity.subtitle}
        details={p.identity.details}
        elapsedAnchorMs={p.identity.elapsedAnchorMs}
        recording={consent ? consent.ui.capturing : p.recording}
      />

      <div className="cl:flex cl:min-h-0 cl:flex-1">
        <div className="cl:relative cl:min-h-0 cl:min-w-0 cl:flex-1">
          <EquipmentStage
            role="worker"
            layout="desktop"
            waitingLabel={p.inviteLabel ?? p.identity.title}
            thumbnailsCollapsed={p.thumbnailsCollapsed}
            renderStageOverlay={
              sync != null ? (t) => <StageSyncLayer layout="desktop" stageTrack={t} /> : undefined
            }
            onToggleThumbnails={p.onToggleThumbnails}
            onUserSelectedStage={sync != null ? handleUserSelectedStage : undefined}
          />
          {consent && <RecordingConsentCard consent={consent} layout="desktop" />}
        </div>

        {p.sidePanel != null && p.sidePanelOpen && p.isWide && (
          <aside className="cl:w-[360px] cl:shrink-0 cl:overflow-y-auto cl:border-l cl:border-white/10 cl:bg-background cl:text-foreground cl:p-4">
            {p.sidePanel}
          </aside>
        )}
      </div>

      <CallBar
        layout="desktop"
        canEndSession={p.canEndSession}
        endSessionPending={p.endSessionPending}
        sidePanelLabel={p.sidePanelLabel}
        sidePanelOpen={p.sidePanelOpen}
        thumbnailsCollapsed={p.thumbnailsCollapsed}
        onLeave={() => room.disconnect()}
        onEndSession={p.onEndSession}
        onToggleSidePanel={
          p.sidePanel != null ? () => p.onSidePanelOpenChange(!p.sidePanelOpen) : undefined
        }
        onToggleThumbnails={p.onToggleThumbnails}
      />

      {p.sidePanel != null && !p.isWide && (
        <Sheet open={p.sidePanelOpen} onOpenChange={p.onSidePanelOpenChange}>
          <SheetContent side="bottom" className="cl:max-h-[80dvh] cl:overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{p.sidePanelLabel ?? 'Notes'}</SheetTitle>
            </SheetHeader>
            {p.sidePanel}
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}

/**
 * The worker-facing remote-support room (D-01 through D-04, D-06, D-09): slim identity header,
 * pinned equipment stage with collapsible thumbnails, a side-panel slot (notes, 04-06) that
 * becomes a right-hand column at 1024px and wider and a bottom Sheet below that, the labelled
 * call bar, and an end-of-call prompt slot that replaces the room once this browser's own End
 * session call has succeeded (04-08 fills the prompt content).
 *
 * Keeps Phase 3's connection semantics exactly, via useVideoRoomConnection: Leave is always a
 * LiveKit disconnect with no API call, End session is worker-only and goes through the hook's
 * mutation, and the popup's postMessage('call-ended') channel is driven by the caller's onEnded.
 */
export function RemoteSupportRoom(p: {
  token: string
  serverUrl: string
  orgId?: string | null
  callId?: string | null
  inviteLabel?: string
  recording: boolean
  identity: RoomIdentity
  sidePanel?: ReactNode
  sidePanelLabel?: string
  renderAfterEndSession?: (done: () => void) => ReactNode
  onEnded: (info: VideoRoomEndedInfo) => void
}) {
  const conn = useVideoRoomConnection({
    orgId: p.orgId,
    callId: p.callId,
    onEnded: p.onEnded,
    deferEndAfterEndSession: p.renderAfterEndSession != null,
  })

  const layoutContext = useCreateLayoutContext()
  const isWide = useIsWide()
  const [thumbnailsCollapsed, setThumbnailsCollapsed] = useState(false)
  const [sidePanelOpen, setSidePanelOpen] = useState(isWide)

  // Runs the sync core through the org-scoped admin API only when this room has org context
  // (D-01): the public /video-room route without orgId/callId stays without a sync layer.
  const syncApi = useMemo(
    () => (p.orgId && p.callId ? workerSyncApi(Number(p.orgId), Number(p.callId)) : null),
    [p.orgId, p.callId],
  )

  // D-09: the worker room reaches only the org-scoped admin consent API, and only when this room
  // has org context. A token that withholds publishing (D-06) keeps the camera and microphone down
  // until the server grants publish.
  const consentApi = useMemo(
    () =>
      p.orgId && p.callId
        ? workerRecordingConsentApi(Number(p.orgId), Number(p.callId))
        : null,
    [p.orgId, p.callId],
  )
  const held = useMemo(() => !tokenCanPublish(p.token), [p.token])

  if (conn.sessionEndedByMe && p.renderAfterEndSession) {
    // LiveKitRoom unmounts here; the hook's handleDisconnected already ignores the resulting
    // disconnect event because sessionEndedByMeRef is set (D-09).
    return (
      <ShadcnScope className="cl:relative cl:flex cl:h-dvh cl:items-center cl:justify-center cl:bg-neutral-950">
        {p.renderAfterEndSession(conn.finishAfterEndSession)}
      </ShadcnScope>
    )
  }

  if (!p.token || !p.serverUrl) {
    return (
      <ShadcnScope className="cl:relative cl:flex cl:h-dvh cl:items-center cl:justify-center cl:bg-neutral-950">
        <RoomBlockingCard title="Missing video credentials">
          This link is incomplete. Please open the invite link again.
        </RoomBlockingCard>
      </ShadcnScope>
    )
  }

  const media = conn.mediaFailure != null ? mediaFailureMessage(conn.mediaFailure) : null

  const workerRoomBody = (
    <WorkerRoomBody
      identity={p.identity}
      recording={p.recording}
      consentApi={consentApi}
      token={p.token}
      inviteLabel={p.inviteLabel}
      sidePanel={p.sidePanel}
      sidePanelLabel={p.sidePanelLabel}
      sidePanelOpen={sidePanelOpen}
      isWide={isWide}
      thumbnailsCollapsed={thumbnailsCollapsed}
      canEndSession={conn.canEndSession}
      endSessionPending={conn.endSessionPending}
      onSidePanelOpenChange={setSidePanelOpen}
      onToggleThumbnails={() => setThumbnailsCollapsed((v) => !v)}
      onEndSession={conn.requestEndSession}
      onMediaFailure={conn.reportMediaFailure}
    />
  )

  return (
    <ShadcnScope className="cl:relative cl:flex cl:h-dvh cl:flex-col cl:bg-neutral-950 cl:text-white">
      <LiveKitRoom
        key={conn.attempt}
        token={p.token}
        serverUrl={p.serverUrl}
        connect={true}
        video={!held}
        audio={!held}
        className="cl:flex cl:min-h-0 cl:flex-1 cl:flex-col"
        onConnected={conn.handleConnected}
        onDisconnected={conn.handleDisconnected}
        onError={conn.handleError}
        onMediaDeviceFailure={conn.handleMediaDeviceFailure}
      >
        <AutoEnableTracks enabled={!held} onFailure={conn.reportMediaFailure} />
        <LayoutContextProvider value={layoutContext}>
          {syncApi != null ? (
            <RoomSyncProvider api={syncApi}>{workerRoomBody}</RoomSyncProvider>
          ) : (
            workerRoomBody
          )}
        </LayoutContextProvider>
        <RoomAudioRenderer />
      </LiveKitRoom>

      {conn.endSessionError != null && (
        <RoomBlockingCard
          title="Could not end the session"
          actionLabel="OK"
          onAction={conn.clearEndSessionError}
        >
          {conn.endSessionError}
        </RoomBlockingCard>
      )}

      {conn.endConfirm && (
        <RoomBlockingCard
          title="A customer is still in this call"
          actionLabel="End anyway"
          secondaryLabel="Keep the call"
          actionDisabled={conn.endSessionPending}
          onAction={conn.confirmEndSession}
          onSecondary={conn.cancelEndConfirm}
        >
          Ending the session will disconnect them.
        </RoomBlockingCard>
      )}

      {media != null && (
        <RoomBlockingCard title={media.title} actionLabel="Try again" onAction={conn.retry}>
          {media.body}
        </RoomBlockingCard>
      )}

      {media == null && conn.roomError != null && (
        <RoomBlockingCard title="Could not connect" actionLabel="Try again" onAction={conn.retry}>
          {conn.roomError.message ||
            'Something went wrong while connecting to the video call.'}
        </RoomBlockingCard>
      )}

      {media == null && conn.roomError == null && conn.failedBeforeConnecting && (
        <RoomBlockingCard title="Could not connect" actionLabel="Try again" onAction={conn.retry}>
          The video service closed the connection before the call started. Check that the video
          server is reachable from this device.
        </RoomBlockingCard>
      )}
    </ShadcnScope>
  )
}

export default RemoteSupportRoom
